import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, Hash, Calendar, Plane, ClipboardList, CreditCard, Banknote, Home as HomeIcon } from 'lucide-react';
import { useAppStore } from '../store';
import PhotoGallery from '../components/PhotoGallery';
import RoomDetails from '../components/RoomDetails';
import BookingHint from '../components/BookingHint';
import {
  ArrivalDepartureDisplay,
  HouseRulesDisplay,
  PaymentTermsDisplay,
  PricingDisplay,
} from '../components/content';
import StarRating from '@/components/StarRating';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import BookingCalendar from '../components/BookingCalendar';
import type { BookingRequest } from '../types';
import { calculatePriceBreakdown, getCurrentSeason, getWeeklyRate } from '../utils/seasonUtils';

const HomeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { homes, createBooking, checkAvailability, currentUser, getAverageRating, getReviewsForHome, familyBookingInfo } = useAppStore();

  const home = homes.find(h => h.id === id);

  // Booking state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{ available: boolean; message?: string } | null>(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState<{ referenceNumber: string; homeName: string; referenceNumber2?: string; home2Name?: string } | null>(null);
  const [guests, setGuests] = useState(1);
  const [bookBothHomes, setBookBothHomes] = useState(false);
  const [otherHomeAvailability, setOtherHomeAvailability] = useState<{ available: boolean; message: string } | null>(null);
  const [cleaningOption, setCleaningOption] = useState<'self' | 'service'>('self');

  const hasAccess = currentUser?.isAdmin ||
    !currentUser?.allowedHomeIds?.length ||
    (home && currentUser.allowedHomeIds!.includes(home.id));

  if (!home || !hasAccess) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h1 className="text-2xl font-bold mb-4">{!home ? 'Property not found' : 'Kein Zugriff'}</h1>
        <Button onClick={() => navigate('/homes')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Button>
      </div>
    );
  }

  const reviews = getReviewsForHome(home.id);
  const averageRating = getAverageRating(home.id);

  const isGuestUser = !currentUser?.isAdmin && !currentUser?.isFamily && !currentUser?.isFamilyMember;
  const isOwner = Boolean(currentUser?.isAdmin || currentUser?.isFamily);
  const otherHome = isOwner ? homes.find(h => h.id !== home.id) : undefined;

  const addDays = (dateStr: string, n: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };

  const handleStartDateChange = (raw: string) => {
    if (!raw) { setStartDate(''); setEndDate(''); setAvailabilityStatus(null); return; }
    setStartDate(raw);
    setEndDate('');
    setBookingError(null);
    setAvailabilityStatus(null);
  };

  const handleEndDateChange = (raw: string) => {
    if (!raw || !startDate) return;
    const nights = calculateNights(startDate, raw);
    if (isGuestUser) {
      if (nights < 3) {
        setBookingError('Mindestaufenthalt für Gäste: 3 Nächte.');
        setEndDate(raw);
        setAvailabilityStatus(null);
        return;
      }
    } else {
      if (nights <= 0) {
        setBookingError('Abreise muss nach der Anreise liegen.');
        setEndDate(raw);
        setAvailabilityStatus(null);
        return;
      }
    }
    setEndDate(raw);
    setBookingError(null);
    setAvailabilityStatus(null);
    triggerAvailabilityCheck(startDate, raw);
  };

  const checkOtherHomeAvailability = async (start: string, end: string) => {
    if (!otherHome) return;
    try {
      const result = await checkAvailability(otherHome.id, new Date(start), new Date(end));
      setOtherHomeAvailability({
        available: result.available,
        message: result.available
          ? `${otherHome.name} ist verfügbar.`
          : result.blockedReason || `${otherHome.name} ist nicht verfügbar.`,
      });
    } catch {
      setOtherHomeAvailability({ available: false, message: 'Verfügbarkeit konnte nicht geprüft werden.' });
    }
  };

  const handleBookBothToggle = (checked: boolean) => {
    setBookBothHomes(checked);
    if (checked && startDate && endDate) {
      checkOtherHomeAvailability(startDate, endDate);
    } else {
      setOtherHomeAvailability(null);
    }
  };

  const handleCloseBookingModal = () => {
    setIsBookingModalOpen(false);
    setBookBothHomes(false);
    setOtherHomeAvailability(null);
    setCleaningOption('self');
  };

  const triggerAvailabilityCheck = async (start: string, end: string) => {
    setIsCheckingAvailability(true);
    try {
      const [result] = await Promise.all([
        checkAvailability(home.id, new Date(start), new Date(end)),
      ]);
      if (result.available) {
        setAvailabilityStatus({ available: true, message: 'Zeitraum ist verfügbar.' });
      } else {
        setAvailabilityStatus({ available: false, message: result.blockedReason || 'Zeitraum nicht verfügbar.' });
      }
      if (bookBothHomes && otherHome) {
        await checkOtherHomeAvailability(start, end);
      }
    } catch {
      setAvailabilityStatus({ available: false, message: 'Verfügbarkeit konnte nicht geprüft werden.' });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const submitBooking = async () => {
    if (!startDate || !endDate || !currentUser) return;

    setIsSubmitting(true);
    setBookingError(null);

    const baseRequest: BookingRequest = {
      homeId: home.id,
      userId: currentUser.id,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      guests: currentUser.isFamilyMember ? guests : undefined,
      cleaningOption: (isOwner || currentUser.isFamilyMember) ? cleaningOption : undefined,
    };

    const result = await createBooking(baseRequest);

    if (result.success && result.referenceNumber) {
      let referenceNumber2: string | undefined;

      if (bookBothHomes && otherHome) {
        const result2 = await createBooking({
          ...baseRequest,
          homeId: otherHome.id,
        });
        if (result2.success && result2.referenceNumber) {
          referenceNumber2 = result2.referenceNumber;
        }
      }

      setBookingSuccess({
        referenceNumber: result.referenceNumber,
        homeName: home.name,
        referenceNumber2,
        home2Name: otherHome?.name,
      });
      setIsBookingModalOpen(false);
      setStartDate('');
      setEndDate('');
      setAvailabilityStatus(null);
      setOtherHomeAvailability(null);
      setBookBothHomes(false);
    } else {
      setBookingError(result.error || 'Failed to create booking');
    }

    setIsSubmitting(false);
  };

  const calculateNights = (start: string, end: string) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    return Math.ceil(Math.abs(e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  };

  const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sc = home.pricingStructure.seasonalConfig as any;
  const hasStoredSeasons = Array.isArray(sc?.seasons);
  const hasNewFormat = !hasStoredSeasons && (sc?.highSeasonRates || sc?.lowSeasonRates);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fmtRange = (s: any) => `${s.startDay}. ${monthNames[s.startMonth - 1]} – ${s.endDay}. ${monthNames[s.endMonth - 1]}`;

  // Old format
  const highSeason = hasStoredSeasons ? sc.seasons.find((s: any) => s.type === 'high') : null;
  const lowSeasons: any[] = hasStoredSeasons
    ? [
        ...sc.seasons.filter((s: any) => s.type === 'low'),
        ...(sc.defaultSeason?.type === 'low' ? [sc.defaultSeason] : []),
      ]
    : [];

  // New format (SeasonalPricingEditor)
  const newHighRanges: any[] = hasNewFormat ? (sc.highSeasonRanges ?? []) : [];
  const newLowRanges: any[] = hasNewFormat ? (sc.lowSeasonRanges ?? []) : [];
  const newHighRate: number = hasNewFormat ? (sc.highSeasonRates?.weekRate ?? 0) : 0;
  const newLowRate: number = hasNewFormat ? (sc.lowSeasonRates?.weekRate ?? 0) : 0;

  const explicitLowRanges = home.pricingStructure.seasonalConfig?.lowSeasonRanges ?? [];
  const lowSeasonLabel = hasNewFormat
    ? newLowRanges.map(fmtRange).join(' sowie ')
    : explicitLowRanges.length > 0
      ? explicitLowRanges.map(fmtRange).join(' sowie ')
      : lowSeasons.map(fmtRange).join(' sowie ');

  const getBookingWeeklyRate = (start: string): number => {
    if (!start) return 0;
    if (hasStoredSeasons && highSeason) {
      const d = new Date(start);
      const m = d.getMonth() + 1;
      const day = d.getDate();
      const v = m * 100 + day;
      const hsv = highSeason.startMonth * 100 + highSeason.startDay;
      const hev = highSeason.endMonth * 100 + highSeason.endDay;
      const isHigh = hsv <= hev ? v >= hsv && v <= hev : v >= hsv || v <= hev;
      if (isHigh) return highSeason.rates.weekRate;
      return lowSeasons[0]?.rates.weekRate ?? 0;
    }
    if (home.pricingStructure.seasonalConfig) {
      return getWeeklyRate(new Date(start), home.pricingStructure.seasonalConfig);
    }
    return 0;
  };

  const calculateWeeklyPrice = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const nights = calculateNights(start, end);
    return Math.round(getBookingWeeklyRate(start) * nights);
  };

  // Get current season info if seasonal config exists
  const currentSeasonInfo = useMemo(() => {
    if (home?.pricingStructure.seasonalConfig) {
      return getCurrentSeason(home.pricingStructure.seasonalConfig);
    }
    return null;
  }, [home?.pricingStructure.seasonalConfig]);

  const getRate = () => {
    const isFamily = currentUser?.isFamily ?? false;

    // Use seasonal pricing if available
    if (home.pricingStructure.seasonalConfig && currentSeasonInfo) {
      return isFamily
        ? currentSeasonInfo.rates.familyRate
        : currentSeasonInfo.rates.guestRate;
    }

    // Legacy pricing
    return isFamily
      ? home.pricingStructure.userTypeOverrides?.family || home.pricingStructure.weekdayRate
      : home.pricingStructure.userTypeOverrides?.guest || home.pricingStructure.weekdayRate;
  };

  const calculatePrice = (start: string, end: string) => {
    if (!start || !end) return 0;
    const isFamily = currentUser?.isFamily ?? false;

    // Use seasonal pricing if available
    if (home.pricingStructure.seasonalConfig) {
      const breakdown = calculatePriceBreakdown(
        new Date(start),
        new Date(end),
        home.pricingStructure.seasonalConfig,
        isFamily
      );
      return breakdown.totalPrice;
    }

    // Legacy pricing
    const nights = calculateNights(start, end);
    return getRate() * nights;
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/homes')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('nav.properties')}
      </Button>

      {/* Photo Gallery */}
      <PhotoGallery images={home.images} alt={home.name} />

      {/* Property Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">{home.name}</h1>
        </div>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2">
            <StarRating rating={averageRating} size="sm" />
            <span className="text-muted-foreground">
              ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
            </span>
          </div>
        )}
        {/* Pricing below name */}
        {hasNewFormat ? (
          <div className="flex flex-wrap gap-6 pt-1">
            {newHighRate > 0 && (
              <div>
                <div className="text-2xl font-bold">CHF {newHighRate} <span className="text-sm font-normal text-muted-foreground">/ Nacht</span></div>
                <div className="text-sm font-medium">Hochsaison</div>
                {newHighRanges.length > 0 && <div className="text-xs text-muted-foreground">{fmtRange(newHighRanges[0])}</div>}
              </div>
            )}
            {newLowRate > 0 && (
              <div>
                <div className="text-2xl font-bold">CHF {newLowRate} <span className="text-sm font-normal text-muted-foreground">/ Nacht</span></div>
                <div className="text-sm font-medium">Nebensaison</div>
                <div className="text-xs text-muted-foreground">{lowSeasonLabel}</div>
              </div>
            )}
          </div>
        ) : hasStoredSeasons && highSeason ? (
          <div className="flex flex-wrap gap-6 pt-1">
            <div>
              <div className="text-2xl font-bold">CHF {highSeason.rates.weekRate} <span className="text-sm font-normal text-muted-foreground">/ Nacht</span></div>
              <div className="text-sm font-medium">Hochsaison</div>
              <div className="text-xs text-muted-foreground">{fmtRange(highSeason)}</div>
            </div>
            {lowSeasons.length > 0 && (
              <div>
                <div className="text-2xl font-bold">CHF {lowSeasons[0].rates.weekRate} <span className="text-sm font-normal text-muted-foreground">/ Nacht</span></div>
                <div className="text-sm font-medium">Nebensaison</div>
                <div className="text-xs text-muted-foreground">{lowSeasonLabel}</div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="text-3xl font-bold">CHF {home.pricingStructure.combinedRate || home.pricingStructure.weekdayRate}</div>
            <div className="text-sm text-muted-foreground">/ Nacht</div>
          </div>
        )}
      </div>

      {/* Description */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground leading-relaxed">{home.description}</p>
        </CardContent>
      </Card>


      {/* Room Details */}
      {(home.roomDescription || home.layout) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('property.roomDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            {home.roomDescription
              ? <p className="text-sm text-muted-foreground">{home.roomDescription}</p>
              : home.layout && <RoomDetails layout={home.layout} />
            }
          </CardContent>
        </Card>
      )}

      {/* Guest Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('property.guestInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pricing" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="pricing" className="flex items-center gap-2">
                <Banknote className="h-4 w-4" />
                <span className="hidden sm:inline">{t('content.pricingInfo')}</span>
              </TabsTrigger>
              <TabsTrigger value="payment" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">{t('content.paymentTerms')}</span>
              </TabsTrigger>
              <TabsTrigger value="arrival" className="flex items-center gap-2">
                <Plane className="h-4 w-4" />
                <span className="hidden sm:inline">{t('content.arrivalDeparture')}</span>
              </TabsTrigger>
              <TabsTrigger value="rules" className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                <span className="hidden sm:inline">{t('content.houseRules')}</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pricing" className="mt-6">
              <PricingDisplay
                pricing={home.pricingStructure}
                propertyName={home.name}
                cleaningFee={home.structuredContent?.paymentTerms?.cleaningFee}
              />
            </TabsContent>
            <TabsContent value="payment" className="mt-6">
              <PaymentTermsDisplay terms={home.structuredContent?.paymentTerms} />
            </TabsContent>
            <TabsContent value="arrival" className="mt-6">
              <ArrivalDepartureDisplay info={home.structuredContent?.arrivalDeparture} />
            </TabsContent>
            <TabsContent value="rules" className="mt-6">
              <HouseRulesDisplay rules={home.structuredContent?.houseRules} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Booking CTA */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-1">Jetzt buchen?</h3>
              <p className="text-muted-foreground">
                {t('booking.paymentHint')}
              </p>
            </div>
            <Button size="lg" onClick={() => setIsBookingModalOpen(true)}>
              <Calendar className="h-5 w-5 mr-2" />
              {t('booking.bookNow')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={(open) => { if (!open) handleCloseBookingModal(); else setIsBookingModalOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col gap-0 p-0">
          <DialogHeader className="p-6 pb-4 border-b">
            <DialogTitle>Buchung – {home.name}</DialogTitle>
            <DialogDescription>
              {isGuestUser
                ? 'Mindestaufenthalt 3 Nächte. Anreise- und Abreisedatum frei wählbar.'
                : 'Anreise- und Abreisedatum frei wählbar.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Anreise</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  min={getTodayDate()}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Abreise</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  min={startDate ? addDays(startDate, isGuestUser ? 3 : 1) : getTodayDate()}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
              </div>
            </div>

            {isGuestUser && (
              <p className="text-xs text-muted-foreground">
                Mindestaufenthalt: 3 Nächte. Anreise an jedem beliebigen Tag möglich.
              </p>
            )}

            {/* Visual calendar showing booked dates */}
            <BookingCalendar
              homeId={home.id}
              selectedStart={startDate}
              selectedEnd={endDate}
              onSelectStart={handleStartDateChange}
              onSelectEnd={handleEndDateChange}
              mode={isGuestUser ? 'saturday' : 'flexible'}
            />

            {isCheckingAvailability && (
              <p className="text-sm text-muted-foreground">Verfügbarkeit wird geprüft…</p>
            )}

            {availabilityStatus && (
              <div className={`p-3 rounded-md text-sm ${
                availabilityStatus.available
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {availabilityStatus.message}
              </div>
            )}

            {/* Book both homes – only for admin/owner when a second home exists */}
            {isOwner && otherHome && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/40">
                  <Checkbox
                    id="bookBoth"
                    checked={bookBothHomes}
                    onCheckedChange={(checked) => handleBookBothToggle(Boolean(checked))}
                  />
                  <label htmlFor="bookBoth" className="text-sm cursor-pointer flex items-center gap-2">
                    <HomeIcon className="h-4 w-4 text-muted-foreground" />
                    Auch <span className="font-medium">{otherHome.name}</span> für denselben Zeitraum buchen
                  </label>
                </div>
                {bookBothHomes && otherHomeAvailability && (
                  <div className={`p-3 rounded-md text-sm ${
                    otherHomeAvailability.available
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {otherHomeAvailability.message}
                  </div>
                )}
              </div>
            )}

            {bookingError && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
                {bookingError}
              </div>
            )}

            {currentUser?.isFamilyMember && (
              <div className="space-y-2">
                <Label htmlFor="guests">Anzahl Personen</Label>
                <Input
                  id="guests"
                  type="number"
                  min={1}
                  value={guests}
                  onChange={e => setGuests(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24"
                />
              </div>
            )}

            {(isOwner || currentUser?.isFamilyMember) && (home.structuredContent?.paymentTerms?.cleaningFee ?? 0) > 0 && (
              <div className="space-y-2 p-3 rounded-lg border bg-muted/40">
                <Label className="text-sm font-medium">Reinigung</Label>
                <RadioGroup value={cleaningOption} onValueChange={(v) => setCleaningOption(v as 'self' | 'service')} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="self" id="clean-self" />
                    <label htmlFor="clean-self" className="text-sm cursor-pointer">Ich reinige selbst</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="service" id="clean-service" />
                    <label htmlFor="clean-service" className="text-sm cursor-pointer">
                      Reinigungsdienst beauftragen <span className="text-muted-foreground">(+CHF {home.structuredContent?.paymentTerms?.cleaningFee} Reinigungspauschale)</span>
                    </label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {startDate && endDate && calculateNights(startDate, endDate) > 0 && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                {currentUser?.isFamily || currentUser?.isAdmin ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">{calculateNights(startDate, endDate)} Nächte</span>
                      <span className="text-xl font-bold text-green-600">Kostenlos</span>
                    </div>
                    {cleaningOption === 'service' && (home.structuredContent?.paymentTerms?.cleaningFee ?? 0) > 0 && (
                      <div className="flex justify-between items-center border-t pt-2">
                        <span className="text-sm">Reinigungspauschale</span>
                        <span className="text-xl font-bold">CHF {home.structuredContent?.paymentTerms?.cleaningFee}</span>
                      </div>
                    )}
                  </>
                ) : currentUser?.isFamilyMember ? (
                  <>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{calculateNights(startDate, endDate)} Nächte × {guests} {guests === 1 ? 'Person' : 'Personen'} × CHF 20</span>
                    </div>
                    {cleaningOption === 'service' && (home.structuredContent?.paymentTerms?.cleaningFee ?? 0) > 0 && (
                      <div className="flex justify-between items-center text-sm text-muted-foreground">
                        <span>Reinigungspauschale</span>
                        <span>CHF {home.structuredContent?.paymentTerms?.cleaningFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-xl font-bold">CHF {calculateNights(startDate, endDate) * guests * 20 + (cleaningOption === 'service' ? (home.structuredContent?.paymentTerms?.cleaningFee ?? 0) : 0)}</span>
                    </div>
                  </>
                ) : (hasStoredSeasons && highSeason) || hasNewFormat ? (
                  <>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>
                        {hasNewFormat
                          ? (getBookingWeeklyRate(startDate) === newHighRate ? 'Hochsaison' : 'Nebensaison')
                          : (getBookingWeeklyRate(startDate) === highSeason.rates.weekRate ? 'Hochsaison' : 'Nebensaison')
                        }
                      </span>
                      <span>CHF {getBookingWeeklyRate(startDate)} / Nacht (Saisonrate)</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{calculateNights(startDate, endDate)} {calculateNights(startDate, endDate) === 1 ? 'Nacht' : 'Nächte'}</span>
                      <span>CHF {calculateWeeklyPrice(startDate, endDate)}</span>
                    </div>
                    {(home.structuredContent?.paymentTerms?.cleaningFee ?? 0) > 0 && (
                      <div className="flex justify-between items-center text-sm text-muted-foreground">
                        <span>Reinigungspauschale</span>
                        <span>CHF {home.structuredContent?.paymentTerms?.cleaningFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-xl font-bold">CHF {calculateWeeklyPrice(startDate, endDate) + (home.structuredContent?.paymentTerms?.cleaningFee ?? 0)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{calculateNights(startDate, endDate)} Nächte × CHF {getRate()}</span>
                      <span>CHF {calculatePrice(startDate, endDate)}</span>
                    </div>
                    {(home.structuredContent?.paymentTerms?.cleaningFee ?? 0) > 0 && (
                      <div className="flex justify-between items-center text-sm text-muted-foreground">
                        <span>Reinigungspauschale</span>
                        <span>CHF {home.structuredContent?.paymentTerms?.cleaningFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-xl font-bold">CHF {calculatePrice(startDate, endDate) + (home.structuredContent?.paymentTerms?.cleaningFee ?? 0)}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            <BookingHint />
          </div>

          <DialogFooter className="p-6 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseBookingModal}>
              Abbrechen
            </Button>
            <Button
              onClick={submitBooking}
              disabled={
                !startDate || !endDate || !availabilityStatus?.available || isSubmitting ||
                (bookBothHomes && otherHome != null && !otherHomeAvailability?.available)
              }
            >
              {isSubmitting
                ? 'Wird gebucht…'
                : bookBothHomes && otherHome
                  ? 'Beide Häuser anfragen'
                  : 'Buchung anfragen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Success Modal */}
      <Dialog open={!!bookingSuccess} onOpenChange={() => setBookingSuccess(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              {bookingSuccess?.referenceNumber2 ? 'Beide Buchungen eingereicht!' : 'Buchung eingereicht!'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-muted-foreground">
              {bookingSuccess?.referenceNumber2
                ? 'Deine Buchungsanfragen für beide Häuser wurden erfolgreich übermittelt.'
                : 'Deine Buchungsanfrage wurde erfolgreich übermittelt.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Hash className="h-5 w-5 text-muted-foreground" />
                <span className="font-mono font-medium">{bookingSuccess?.referenceNumber}</span>
                {bookingSuccess?.referenceNumber2 && (
                  <span className="text-sm text-muted-foreground ml-auto">{bookingSuccess.homeName}</span>
                )}
              </div>
              {bookingSuccess?.referenceNumber2 && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Hash className="h-5 w-5 text-muted-foreground" />
                  <span className="font-mono font-medium">{bookingSuccess.referenceNumber2}</span>
                  <span className="text-sm text-muted-foreground ml-auto">{bookingSuccess.home2Name}</span>
                </div>
              )}
            </div>
            <BookingHint variant="compact" />
            {(isOwner || currentUser?.isFamilyMember) && familyBookingInfo && (
              <div className="rounded-lg border bg-amber-50 border-amber-200 space-y-2">
                <p className="text-sm font-semibold text-amber-800 px-4 pt-4">Wichtige Infos für euren Aufenthalt</p>
                <div className="overflow-y-auto max-h-48 px-4 pb-4 prose prose-sm max-w-none text-amber-900">
                  <ReactMarkdown>{familyBookingInfo}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingSuccess(null)}>
              {t('common.close')}
            </Button>
            <Link to="/bookings">
              <Button onClick={() => setBookingSuccess(null)}>
                Meine Buchungen
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeDetailPage;
